<script lang="ts">
  // Overview > Flags — the per-person attendance queue (GET /api/hr/flags).
  //
  // The sibling of Stats, and deliberately a different object: Stats
  // describes the org and names nobody, this page names people. Everything
  // below follows from that. It is a queue of conversations to have, not a
  // ranking: there is no score, no "mark reviewed", no export, and every
  // card carries the threshold it crossed and what was excluded to get
  // there. A flag lives while the behaviour does and disappears with it.
  //
  // People evaluated is on the hero strip for the same reason: without the
  // denominator, "nothing crossed a threshold" and "nobody was measured"
  // render identically, and only one of those is good news.
  import { createQuery } from '@tanstack/svelte-query';
  import { hrFlagsApi, type HRInsightRangeKey } from '$lib/api/hr';
  import { fieldApi } from '$lib/api/field';
  import { session } from '$lib/state/session.svelte';
  import type { HRFlag, HRPersonFlags } from '$lib/types';
  import { route } from '$lib/state/route.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import PageHeader from '$lib/components/ui/PageHeader.svelte';
  import StatRow from '$lib/components/ui/StatRow.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';

  // Same range chips as Stats (StatsPage.svelte) so the two Overview pages
  // answer to the same window vocabulary.
  const RANGES: { id: HRInsightRangeKey; label: string }[] = [
    { id: '7d', label: 'Week' },
    { id: '30d', label: 'Month' },
    { id: '90d', label: 'Quarter' },
    { id: '365d', label: 'Year' }
  ];

  const SEVERITIES = [
    { id: 'all', label: 'All' },
    { id: 'warning', label: 'Warnings' },
    { id: 'notice', label: 'Notices' }
  ];

  // Type families group the nine detectors into the five things HR actually
  // asks about. early_leave_pattern sits under Late: both are the same
  // office-hours baseline, measured at the other end of the day.
  const FAMILIES: { id: string; label: string; types: string[] }[] = [
    { id: 'all', label: 'All', types: [] },
    { id: 'late', label: 'Late', types: ['late_pattern', 'late_trend', 'early_leave_pattern'] },
    { id: 'absence', label: 'Absence', types: ['unreconciled_absence'] },
    { id: 'leave', label: 'Leave', types: ['leave_pace', 'leave_peer_outlier', 'leave_pattern'] },
    { id: 'requests', label: 'Requests', types: ['request_spike'] },
    { id: 'integrity', label: 'Integrity', types: ['integrity_forgot'] }
  ];

  const TYPE_LABEL: Record<string, string> = {
    late_pattern: 'Late arrivals',
    late_trend: 'Arrival drift',
    unreconciled_absence: 'Unexplained days',
    integrity_forgot: 'Auto-closed punches',
    leave_pace: 'Leave pace',
    leave_peer_outlier: 'Leave vs team',
    leave_pattern: 'Leave shape',
    request_spike: 'Filing volume',
    early_leave_pattern: 'Early departures'
  };

  let range = $state<HRInsightRangeKey>('30d');
  let severity = $state('all');
  let family = $state('all');

  const flags = createQuery(() => ({
    queryKey: [session.isFieldScoped ? 'field-flags' : 'hr-flags', range],
    queryFn: () => session.isFieldScoped ? fieldApi.flags(range) : hrFlagsApi.get(range)
  }));

  const rangeNoun = $derived(
    ({ '7d': 'week', '30d': 'month', '90d': 'quarter', '365d': 'year' } as const)[range]
  );

  function typeLabel(type: string): string {
    return TYPE_LABEL[type] ?? type.replace(/_/g, ' ');
  }

  // Both filters are client-side and subtractive: they narrow the flags
  // first, then drop anyone left with none. A person card that survives a
  // "Warnings" filter always shows only their warnings, never a warning
  // headline over a notice's evidence.
  function visibleFlags(person: HRPersonFlags): HRFlag[] {
    const types = FAMILIES.find((f) => f.id === family)?.types ?? [];
    return person.flags.filter(
      (f) => (severity === 'all' || f.severity === severity) && (types.length === 0 || types.includes(f.type))
    );
  }

  const people = $derived(
    (flags.data?.people ?? [])
      .map((p) => ({ person: p, shown: visibleFlags(p) }))
      .filter((row) => row.shown.length > 0)
  );

  function lampTone(sev: string): 'good' | 'warn' | 'bad' | 'idle' {
    if (sev === 'warning') return 'bad';
    if (sev === 'notice') return 'warn';
    return 'idle';
  }

  function openPerson(userId: number) {
    route.push({ tab: 'directory', sub: 'person', userId });
  }
</script>

<div class="tym-page">
  <PageHeader
    title="Attendance flags"
    subtitle="People whose punches or leave stand out this period — for a conversation, not a verdict."
  />

  {#if flags.data}
    <!-- The page states its own limits, in-product. Everything here is a
         pattern in punch data; none of it is a reason, and only the two
         numbers named below are company policy. -->
    <p class="mt-2 text-[11.5px] leading-relaxed text-dark-gray">
      Measured against the {flags.data.baseline.start}–{flags.data.baseline.end} office-hours baseline, with no grace
      period. Weekends, holidays, approved leave, field days and days outside someone's employment are excluded.
      Thresholds are configured defaults, not company policy — each flag states the number it used. Flags describe
      patterns, never reasons.
    </p>
  {/if}

  <FilterChipRow
    class="mb-3 mt-4"
    chips={RANGES}
    value={range}
    onSelect={(id) => (range = id as HRInsightRangeKey)}
    ariaLabel="Flag range"
  />
  <FilterChipRow
    class="mb-3"
    chips={SEVERITIES}
    value={severity}
    onSelect={(id) => (severity = id)}
    ariaLabel="Severity filter"
  />
  <FilterChipRow
    class="mb-5"
    chips={FAMILIES.map((f) => ({ id: f.id, label: f.label }))}
    value={family}
    onSelect={(id) => (family = id)}
    ariaLabel="Flag type filter"
  />

  {#if flags.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if flags.isError}
    <div class="tym-error-state">Could not load attendance flags.</div>
  {:else if flags.data}
    {@const summary = flags.data.summary}

    <div class="mb-6">
      <StatRow
        items={[
          { label: 'People flagged', value: summary.people_flagged },
          { label: 'Warnings', value: summary.by_severity?.warning ?? 0 },
          { label: 'Notices', value: summary.by_severity?.notice ?? 0 },
          { label: 'People evaluated', value: summary.people_evaluated }
        ]}
      />
    </div>

    {#if people.length === 0}
      <div class="tym-card">
        <EmptyState
          title={flags.data.people.length === 0 ? `No flags this ${rangeNoun}.` : 'Nothing matches these filters.'}
          body={flags.data.people.length === 0
            ? `${summary.people_evaluated} ${summary.people_evaluated === 1 ? 'person' : 'people'} evaluated against the ${flags.data.baseline.start} baseline. Nothing crossed a threshold.`
            : 'Widen the severity or type filter to see the rest of the queue.'}
        />
      </div>
    {:else}
      <!-- Cards at every width: this is a queue of people, and a person is a
           card, not a table row. Two-up from sm, single column below. -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {#each people as row (row.person.user_id)}
          {@const top = row.shown[0]}
          <button
            type="button"
            class="tym-card w-full p-4 text-left transition-colors {session.isFieldScoped ? '' : 'hover:bg-surface-2'}"
            disabled={session.isFieldScoped}
            onclick={() => openPerson(row.person.user_id)}
          >
            <div class="flex items-start gap-2.5">
              <span class="mt-1">
                <StatusLamp tone={lampTone(top.severity)} label={top.severity} />
              </span>
              <div class="min-w-0 flex-1">
                <p class="truncate font-heading text-[14.5px] font-bold leading-snug text-navy">
                  {row.person.name}
                </p>
                <p class="mt-0.5 text-[11.5px] text-dark-gray">
                  {row.shown.length}
                  {row.shown.length === 1 ? 'flag' : 'flags'} · {row.person.expected_days} working days evaluated
                </p>
              </div>
            </div>

            <p class="mt-2.5 text-[13px] font-semibold leading-snug text-navy">{top.title}</p>
            <p class="mt-1 text-[12.5px] leading-relaxed text-dark-gray">{top.evidence}</p>

            <div class="mt-3 flex flex-wrap gap-1.5">
              {#each row.shown as f (f.id)}
                <span
                  class="rounded-full border border-toyama-cloudy-gray bg-surface-2 px-2 py-0.5 text-[10.5px] font-semibold text-dark-gray"
                >
                  {typeLabel(f.type)}
                </span>
              {/each}
            </div>
          </button>
        {/each}
      </div>
    {/if}
  {/if}
</div>
